import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Wallet } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Cashdesk } from 'shared/api';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormButtonProps, ModalFormProps } from 'shared/ui';
import { WithdrawCashdeskModel } from '../../model';

export const createWithdrawCashdeskModal = (model: WithdrawCashdeskModel) => {
	const ModalForm = createModalForm(model);

	const AmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'withdraw-cashdesk-amount-input',
			value: model.form.fields.amount.$value,
			onChange: (e) => model.form.fields.amount.change(`${e}`),
			label: translated('withdraw-cashdesk.amount'),
			rightSection: model.$modalData.map((cashdesk) => cashdesk?.currency ?? null),
			placeholder: '250.00',
			errorProps: { 'data-test-id': 'withdraw-cashdesk-amount-input-error' },
			error: combine(
				[model.form.fields.amount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('withdraw-cashdesk.amount'),
					}),
			),
			required: true,
		},
	});

	const WithdrawCashdeskModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				ref={ref}
				title={t('withdraw-cashdesk.title')}
				submitButtonProps={{
					children: t('withdraw-cashdesk.withdraw'),
					loading: pending,
				}}
				cancelButtonProps={{
					children: t('withdraw-cashdesk.cancel'),
				}}
				{...props}
			>
				<AmountField />
			</ModalForm>
		);
	});

	const WithdrawCashdeskModalButton = forwardRef<HTMLButtonElement, ModalFormButtonProps<Cashdesk>>(
		({ modalData, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<ModalForm.Button
					ref={ref}
					data-test-id='withdraw-cashdesk-button'
					leftSection={<Wallet />}
					disabled={modalData.isLocked}
					modalData={modalData}
					{...props}
				>
					{t('withdraw-cashdesk.withdraw')}
				</ModalForm.Button>
			);
		},
	);

	return Object.assign(WithdrawCashdeskModal, {
		Button: WithdrawCashdeskModalButton,
	});
};
